// src/calls/index.js — 📹 the Calls tab (v126.11). Weekly area calls without
// the lost-link dance: the app derives each area's permanent Jitsi room from
// the Worker (/api/calls/rooms), so "join the area call" is one tap, forever.
//
// Free by design: calls run on the public meet.jit.si service embedded via
// Jitsi's official iframe API (their script loads on demand, only when a
// call is opened). The person STARTING a call may be asked by Jitsi to sign
// in once with a free Google/Microsoft account (their moderator rule);
// joiners need nothing. "Open in browser ↗" is the always-works fallback.
//
// Meetings integration (Matt's spec): any open task whose title looks like a
// meeting ("Area meeting 3pm", "conference call", …) is surfaced here with a
// Join button — and the Tasks screen shows the same button on the card.

import { visiblePoll } from '../shared/visible-poll.js';   // v136.8: the missed-calls loop rests while the page is hidden

var JITSI_MATCH = /\b(meeting|call|huddle|briefing|catch[- ]?up)\b/i;

// ── v129.35 — start every call at LOW definition ────────────────────────────
// Matt, 3 Aug: "can the quality be set to 2/4 — this will ensure when we have
// low bandwidth we can still connect."
//
// Position 2 of 4 on Jitsi's own performance slider. Its four stops, read off
// VideoQualitySlider.web.tsx rather than guessed at, are:
//
//     1  audio only   (lowBandwidthMode)
//     2  low          VIDEO_QUALITY_LEVELS.LOW      = 180   <- this
//     3  standard     VIDEO_QUALITY_LEVELS.STANDARD = 360
//     4  high         VIDEO_QUALITY_LEVELS.ULTRA    = 2160
//
// It is a DEFAULT, not a lock. The slider is still in the call menu, so anyone
// on a good connection can push it up for that call; nothing here prevents it.
//
// Both directions are capped, because they fail differently on mall wifi:
//   • `resolution` + `constraints` cap what this device SENDS. Uplink is the
//     scarcer half of a store connection and the half that makes the far end
//     freeze.
//   • setVideoQuality caps what it RECEIVES — the slider's own job. Without it
//     a two-person call still pulls 720p down.
var CALL_QUALITY_HEIGHT = 180;

// v129.124 — this module has NEVER sent an Authorization header, and that is
// why Head Office could not be rung anywhere.
//
// The Worker's resolveIdentity() reads the account from the Bearer, and
// isHeadOfficeCaller(who) requires `who.account`. With no header there is no
// account, so an HO poll resolved to { store: null, account: null }, the ring
// queue came out null, and /api/calls/ring answered `invite: null` forever.
// v129.35 changed the SERVER to serve the HO queue and the CLIENT to stop
// short-circuiting — but the request it then sent could not identify anybody, so
// the feature has been half-wired ever since. Starting a call always worked
// because that goes out through the chat module's authenticated api(), which is
// exactly why nobody noticed: you could call out and never be called back.
//
// Resolved the way db.js does it (line 163): the in-memory token first, then
// `elcAdminToken` — sessionStorage, then the localStorage copy auth.js keeps so
// the session survives iOS evicting the app. The staff app stores the manager
// token ONLY under that key, so DB.getToken() alone would still have missed it.
//
// A till sends nothing extra: a store sign-in never mints an admin session, so
// getToken() is empty and no header goes out. And where a store IS in play the
// `?store=` parameter still decides the queue — resolveIdentity prefers it — so
// the 28 stores' ring routing is byte-for-byte what it was.
function authToken() {
  try {
    var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : '';
    if (tk) return tk;
    if (typeof sessionStorage !== 'undefined') {
      var s = sessionStorage.getItem('elcAdminToken');
      if (s) return s;
    }
    if (typeof localStorage !== 'undefined') {
      var l = localStorage.getItem('elcAdminToken');
      if (l) return l;
    }
  } catch (e) { /* storage blocked — unauthenticated is the old behaviour */ }
  return '';
}

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = authToken();
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}
function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function myStore() {
  try { return (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null; } catch (e) { return null; }
}

var _rooms = null;       // { store, area_id, host, rooms, directory }
var _jaas = null;        // v126.77 — { host, appId, jwt } when the Worker is JaaS-configured; null = free meet.jit.si
// v130.47 — WHEN that token dies. The JaaS JWT the Worker mints lives ~2h;
// `_rooms` (which carries it) was fetched ONCE per page load and cached for
// the life of the page. A store till is a shared device left running all day,
// so by mid-afternoon the cached token was expired and every join was refused
// by 8x8 — deterministically, for that till, while a freshly-loaded page (a
// manager testing it) always worked. That is the "it does not work for them"
// report: not a network fault, an expiry.
var _jaasExp = 0;        // unix seconds, from the token itself

// Pure: read `exp` out of a JWT without verifying it. We are not trusting it —
// the token is 8x8's to validate — we only need to know when to ask for a new
// one. Any parse failure returns 0, which means "refresh before the next join".
export function jwtExpSeconds(jwt) {
  try {
    var part = String(jwt || '').split('.')[1];
    if (!part) return 0;
    var json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    var exp = JSON.parse(json).exp;
    return Number.isFinite(exp) ? exp : 0;
  } catch (e) { return 0; }
}

// Pure: refresh if the token is gone, unreadable, or inside its last 5 minutes.
// The margin matters — a call that starts at exp-30s dies once the token lapses
// mid-meeting, which reads to the user as the call "dropping for no reason".
export function jaasNeedsRefresh(exp, nowMs) {
  if (!exp) return true;
  var now = Math.floor((nowMs == null ? Date.now() : nowMs) / 1000);
  return now > (exp - 300);
}
var _meetings = [];      // meeting-ish open tasks for today
var _myRooms = [];       // v126.63 — the manager's own stores + rooms, joined by index

// v126.63 (Matt, after an area manager asked twice: "can head office and area manager, on the
// index have their own view for calls where they can set them up?"). A manager
// (HO admin / area_manager) signed into the staff app gets a Calls view shaped
// for THEM: the area room at the top, then a list of THEIR stores to drop into,
// then the ad-hoc box. A shop-floor staff member is unchanged. currentUser and
// getMyStores() are index.html globals, read by bare name at call time.
function isManagerView() {
  try {
    return typeof currentUser !== 'undefined' && currentUser &&
      (currentUser.role === 'admin' || currentUser.role === 'area_manager');
  } catch (e) { return false; }
}
// Who answers the Head Office phone, client side. Mirrors isHeadOfficeCaller()
// in src/worker/routes/calls.js.
//
// v129.40 (Matt: "i want all head office people (including area managers) to
// have calling accounts") — area_manager is IN now. v129.35 had excluded them
// on my inference that an AM is not Head Office; at ELC they are, and being
// unreachable was the real problem.
//
// The Worker remains the authority; this only decides whether to bother
// polling, so the two must agree or somebody polls an endpoint that can only
// ever answer empty.
// v129.124 — read BOTH identity shapes. The staff app names its signed-in person
// `currentUser`; chat.html — the standalone app Head Office actually installs —
// names it `window.currentAdmin`, because it runs the ChatAdmin bundle. Checking
// only the first meant the very page HO carries in their pocket reported "not
// Head Office" and never started a ring watch, so the phone could not be rung.
// The Worker remains the authority; this only decides whether to bother polling.
function hoIdentity() {
  try {
    if (typeof currentUser !== 'undefined' && currentUser) return currentUser;
  } catch (e) { /* not this page's global */ }
  try {
    if (typeof window !== 'undefined' && window.currentAdmin) return window.currentAdmin;
  } catch (e) { /* nor this one */ }
  return null;
}

function isHeadOfficeView() {
  try {
    var who = hoIdentity();
    if (!who) return false;
    var r = String(who.role || '').toLowerCase();
    return r === 'admin' || r === 'ho_staff' || r === 'area_manager';
  } catch (e) { return false; }
}

// The manager's stores intersected with the live rooms directory, so we have a
// room name for each. getMyStores() already returns the RIGHT set per role
// (area_manager → their area; admin → all), so scope comes from there, not here.
function myRoomsFromDirectory(directory) {
  var mine = [];
  try { if (typeof getMyStores === 'function') mine = getMyStores() || []; } catch (e) { mine = []; }
  var byName = {};
  (Array.isArray(directory) ? directory : []).forEach(function (s) { if (s && s.name) byName[s.name] = s.room; });
  // include the CURRENT store too — the directory omits it (it's "you")
  if (_rooms && _rooms.store && _rooms.rooms && _rooms.rooms.store) byName[_rooms.store] = _rooms.rooms.store.room;
  return mine.filter(function (n) { return byName[n]; }).sort()
    .map(function (n) { return { name: n, room: byName[n] }; });
}

async function fetchRooms() {
  var st = myStore();
  // v129.35 — Head Office has no store. The Worker reads their Bearer identity
  // and serves the HO room + the full directory, so the request goes out
  // without a ?store=. Anyone else without a store still short-circuits here.
  if (!st && !isHeadOfficeView()) return null;
  try {
    var res = await api('/api/calls/rooms' + (st ? '?store=' + encodeURIComponent(st) : ''));
    if (!res.ok) return null;
    var d = await res.json();
    if (d && d.ok) {
      // v126.77 — the Worker says whether JaaS is live (jwt minted server-side).
      _jaas = (d.jwt && d.app_id) ? { host: d.jaas_host || '8x8.vc', appId: d.app_id, jwt: d.jwt } : null;
      _jaasExp = _jaas ? jwtExpSeconds(_jaas.jwt) : 0;
      _rooms = d; return d;
    }
  } catch (e) { /* offline — the screen says so */ }
  return null;
}

// Meetings ride the EXISTING tasks endpoint (same v125.51 store identity the
// Tasks tab uses) — no new worker surface, and the two screens can't drift.
async function fetchMeetings() {
  var st = myStore();
  if (!st) return [];
  try {
    var res = await api('/api/tasks?store=' + encodeURIComponent(st));
    if (!res.ok) return [];
    var d = await res.json();
    var tasks = (d && d.ok && Array.isArray(d.tasks)) ? d.tasks : [];
    _meetings = tasks.filter(function (t) { return t && !t.completed && JITSI_MATCH.test(String(t.title || '')); });
  } catch (e) { _meetings = []; }
  return _meetings;
}

function meetingRowHtml(t) {
  return '<div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--surface-3)">' +
    '<span style="font-size:18px">📅</span>' +
    '<div style="flex:1;min-width:0"><div style="font-weight:800;font-size:13.5px" translate="no">' + esc(t.title) + '</div>' +
    (t.deadline ? '<div style="font-size:11.5px;color:var(--ui-ink-2)">Due by ' + esc(t.deadline) + '</div>' : '') + '</div>' +
    '<button onclick="Calls.joinArea()" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:9px 14px;border-radius:10px;cursor:pointer">📹 Join</button>' +
    '</div>';
}

function bigButtonHtml(onclick, emoji, title, sub) {
  return '<div onclick="' + onclick + '" style="display:flex;align-items:center;gap:12px;padding:14px;background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;cursor:pointer;margin-bottom:10px">' +
    '<span style="font-size:26px">' + emoji + '</span>' +
    '<div style="flex:1"><div style="font-weight:900;font-size:15px">' + title + '</div>' +
    '<div style="font-size:12px;color:var(--ui-ink-2)">' + sub + '</div></div>' +
    '<span style="font-size:18px;color:var(--comms-brand-text);font-weight:900">›</span></div>';
}

async function render() {
  var el = document.getElementById('callsRoot');
  if (!el) return;
  var st = myStore();
  if (!st && !isHeadOfficeView()) { el.innerHTML = '<div style="padding:24px;text-align:center;color:var(--ui-ink-2)">Sign in to your store to see calls.</div>'; return; }
  if (!_rooms) el.innerHTML = '<div style="padding:24px;text-align:center;color:var(--ui-ink-2)">Loading calls…</div>';
  var d = _rooms || await fetchRooms();
  await fetchMeetings();
  if (!d) { el.innerHTML = '<div style="padding:24px;text-align:center;color:var(--ui-ink-2)">Could not reach the calls service — check the connection and try again.</div>'; return; }

  var html = '<div style="padding:4px 2px 10px">' +
    // v126.77 — with JaaS live the sign-in caveat is untrue: every join
    // carries a Worker-minted token, calls just start.
    '<div style="font-size:12px;color:var(--ui-ink-2);line-height:1.5;padding:0 2px 12px">' + (_jaas
      ? 'Video calls with no links to lose — your area call is always the same room, one tap away. Calls start instantly; no accounts or sign-ins needed.'
      : 'Video calls with no links to lose — your area call is always the same room, one tap away. Whoever starts a call may be asked to sign in once with a free Google account (that&rsquo;s Jitsi&rsquo;s rule for meeting hosts); everyone else just joins.') + '</div>' +
    introHtml() +
    // v129.43 — missed calls ride at the TOP, above every "start a call" card.
    // Somebody opening this screen after a buzz they did not catch is here to
    // find out who wanted them, not to browse the room list.
    missedHtml() +
    netCheckCardHtml();   // v129.75 — 📶 first-line triage for "can't use the video"

  if (_meetings.length) {
    html += '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:4px 14px;margin-bottom:12px">' +
      '<div style="font-size:11px;font-weight:800;color:var(--ui-ink-2);text-transform:uppercase;letter-spacing:.04em;padding:10px 0 2px">📅 Scheduled meetings today</div>' +
      _meetings.map(meetingRowHtml).join('') + '</div>';
  }

  if (d.rooms && d.rooms.area) {
    html += bigButtonHtml('Calls.joinArea()', '📹', esc(d.rooms.area.label), 'Your area&rsquo;s permanent meeting room — weekly calls happen here');
  }
  if (d.rooms && d.rooms.store) {
    html += bigButtonHtml('Calls.joinStore()', d.head_office ? '🏢' : '🏪', esc(d.rooms.store.label),
      d.head_office
        ? 'Head Office&rsquo;s own room — where stores ring you'          // v129.35
        : 'Your store&rsquo;s own room — where other stores call you');
  }

  // v126.16 (Matt: "can head office set up their own with whatever audience
  // they want?") — ad-hoc meetings by NAME: the room derives from the name,
  // so the audience is whoever you tell it to (chat, news, a task). Everyone
  // sees this card; HO announces, people type the name and land together.
  html += '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:10px">' +
    '<div style="font-weight:900;font-size:14px;margin-bottom:6px">🎤 Ad-hoc meeting</div>' +
    '<div style="font-size:12px;color:var(--ui-ink-2);margin-bottom:8px">Type a meeting name — everyone who types the <b>same name</b> joins the <b>same room</b>. Announce it in 💬 Chat or 📰 News to whoever should come.</div>' +
    '<div style="display:flex;gap:8px">' +
      '<input id="callsMeetingName" type="text" maxlength="40" placeholder="e.g. UAE ops catch-up" style="flex:1;padding:10px;border:1.5px solid var(--border,#dde3e8);border-radius:10px;font-family:inherit;font-size:14px">' +
      '<button onclick="Calls.joinCustom()" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:0 16px;border-radius:10px;cursor:pointer">📹 Join</button>' +
    '</div></div>';

  // v126.63 — a MANAGER gets a list of THEIR stores to drop into (each one tap),
  // instead of the single-select "call another store" dropdown a shop-floor
  // staffer sees. _myRooms is kept in module state so the join is by INDEX
  // (injection-safe — room names never ride an onclick string).
  _myRooms = isManagerView() ? myRoomsFromDirectory(d.directory) : [];
  if (_myRooms.length) {
    // v128.47 — 📞 Ring is the primary action now (Teams-style: it pops on
    // their till and they Accept); the old drop-in Join stays as a quiet
    // secondary for "they're expecting me".
    html += '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:10px">' +
      '<div style="font-weight:900;font-size:14px;margin-bottom:6px">🏬 Your stores</div>' +
      '<div style="font-size:12px;color:var(--ui-ink-2);margin-bottom:8px">📞 Ring makes it pop up on their screen like a phone call — they tap Accept and you&rsquo;re talking. Make sure the store is expecting your call — message them first if not.</div>' +
      _myRooms.map(function (s, i) {
        return '<div style="display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--surface-3)">' +
          '<span style="font-size:17px">🏪</span>' +
          '<div style="flex:1;min-width:0;font-weight:700;font-size:13.5px"><span translate="no">' + esc(s.name) + '</span>' +
          ' <a href="#" onclick="Calls.joinMyStore(' + i + ');return false" style="font-size:11px;color:var(--ui-ink-2);font-weight:600">drop in</a></div>' +
          '<button onclick="Calls.dialMyStore(' + i + ')" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:8px 14px;border-radius:10px;cursor:pointer">📞 Ring</button>' +
          '</div>';
      }).join('') + '</div>';
  } else if (Array.isArray(d.directory) && d.directory.length) {
    html += '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:10px">' +
      '<div style="font-weight:900;font-size:14px;margin-bottom:6px">📞 Call another store</div>' +
      '<div style="font-size:12px;color:var(--ui-ink-2);margin-bottom:8px">It rings on their screen like a phone call — they tap Accept and you&rsquo;re talking. Make sure they are expecting your call — message them first if not.</div>' +
      '<select id="callsStorePick" style="width:100%;padding:10px;border:1.5px solid var(--border,#dde3e8);border-radius:10px;font-family:inherit;font-size:14px">' +
      '<option value="">Choose a store…</option>' +
      d.directory.map(function (s) { return '<option value="' + esc(s.name) + '">' + esc(s.name) + '</option>'; }).join('') +
      '</select>' +
      '<button onclick="Calls.dialPicked()" style="width:100%;margin-top:8px;border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:14px;padding:11px;border-radius:10px;cursor:pointer">📞 Ring the store</button></div>';
  }

  html += '</div>';
  el.innerHTML = html;
}

// ── First-time explainer (Matt: "maybe a dialog box first time on what to
// do?") — a dismissible card, shown once per device (localStorage). Says who
// needs an account (the STARTER only) and how to make one on the store's own
// address — the Google verification email lands in the 📧 Email tab, so the
// whole loop happens inside Shift. ──────────────────────────────────────────
var INTRO_KEY = 'elcCallsIntroSeen';

function introSeen() {
  try { return localStorage.getItem(INTRO_KEY) === '1'; } catch (e) { return true; }
}

function introHtml() {
  if (introSeen()) return '';
  // v126.77 — the Google-account steps only apply on the free service; with
  // JaaS live the whole card shrinks to the two things that still matter.
  var steps = _jaas
    ? '<b>1.</b> Joining a call needs nothing — tap the button, allow the camera and mic, you&rsquo;re in. Calls start straight away; nobody signs in to anything.<br>' +
      '<b>2.</b> There are no meeting links to share or lose — your area&rsquo;s room is always behind the same button below.'
    : '<b>1.</b> Joining a call needs nothing — tap the button, allow the camera and mic, you&rsquo;re in.<br>' +
      '<b>2.</b> Only the person who <b>STARTS</b> a call is asked to sign in once, with a free Google account. For weekly area calls that&rsquo;s usually your Area Manager.<br>' +
      '<b>3.</b> Your store can make its own free Google account using its <b>elc…@algt-me.com</b> address — choose &ldquo;Use my current email address&rdquo; when signing up. The verification email arrives right here in Shift, in the 📧 Email tab.<br>' +
      '<b>4.</b> There are no meeting links to share or lose — your area&rsquo;s room is always behind the same button below.';
  return '<div id="callsIntroCard" style="background:var(--ui-green-soft);border:1.5px solid var(--elc-green,#009E3D);border-radius:14px;padding:14px;margin-bottom:12px">' +
    '<div style="font-weight:900;font-size:14px;margin-bottom:6px">👋 First time here? How calls work</div>' +
    '<div style="font-size:12.5px;line-height:1.6;color:var(--comms-ink-soft)">' + steps + '</div>' +
    '<button onclick="Calls.dismissIntro()" style="margin-top:10px;border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:9px 16px;border-radius:10px;cursor:pointer">Got it 👍</button></div>';
}

function dismissIntro() {
  try { localStorage.setItem(INTRO_KEY, '1'); } catch (e) { /* private mode */ }
  render();
}

// ── Joining — the embedded Jitsi overlay ─────────────────────────────────────
var _api = null;          // live JitsiMeetExternalAPI instance
var _scriptLoading = false;

function loadJitsiApi(cb) {
  if (typeof JitsiMeetExternalAPI !== 'undefined') { cb(); return; }
  if (_scriptLoading) { setTimeout(function () { loadJitsiApi(cb); }, 300); return; }
  _scriptLoading = true;
  var s = document.createElement('script');
  // v126.77 — the iframe API script comes from whichever service is live
  // (same API either way; JaaS serves it from 8x8.vc).
  s.src = 'https://' + (_jaas ? _jaas.host : 'meet.jit.si') + '/external_api.js';
  s.onload = function () { _scriptLoading = false; cb(); };
  s.onerror = function () {
    _scriptLoading = false;
    var body = document.getElementById('callsOverlayBody');
    if (body) body.innerHTML = '<div style="padding:30px;text-align:center;color:#fff">Could not load the video service here — use the <b>Open in browser ↗</b> button above instead.</div>';
  };
  document.head.appendChild(s);
}

function logJoin(meta) {
  // Fire-and-forget usage row (v126.16) — a failure must never block a call.
  try {
    var body = { kind: (meta && meta.kind) || 'area', target: (meta && meta.target) || null, store: myStore() };
    api('/api/calls/log', { method: 'POST', body: JSON.stringify(body) }).catch(function () {});
  } catch (e) { /* never blocks */ }
}

// v130.47 — get a live token before we build the iframe. Cheap (one small
// request, only when the cached one is near death) and it removes the whole
// class: no join can now carry a dead token. Re-fetching also refreshes the
// room list, so a store renamed mid-day joins the right room too.
async function ensureLiveJaas() {
  if (!_jaas) return;                       // free meet.jit.si path: no token to age
  if (!jaasNeedsRefresh(_jaasExp)) return;
  try { await fetchRooms(); } catch (e) { /* keep the old token; the join may still work */ }
}

async function joinRoom(room, label, meta) {
  if (!room) return;
  await ensureLiveJaas();
  closeCall();
  logJoin(meta);
  // v126.77 — on JaaS the room lives under the AppID and the join carries a
  // Worker-minted moderator token (short-lived, ~2h), so calls start
  // instantly and the free tier's waiting-for-host warning is untrue — drop
  // it. The ?jwt= browser link is 8x8's own join mechanism for JaaS rooms.
  var browserHref = _jaas
    ? 'https://' + _jaas.host + '/' + _jaas.appId + '/' + encodeURIComponent(room) + '?jwt=' + encodeURIComponent(_jaas.jwt)
    : 'https://meet.jit.si/' + encodeURIComponent(room);
  var overlay = document.createElement('div');
  overlay.id = 'callsOverlay';
  // v136.1 dark mode: the call screen is dark in both themes already (a video stage), so its colours stay as they are.
  overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:#111;display:flex;flex-direction:column';
  overlay.innerHTML =
    '<div style="display:flex;align-items:center;gap:10px;padding:10px 14px;background:#1c1c1e;color:#fff;flex-shrink:0">' +
      '<span style="font-weight:800;font-size:14px;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">📹 <span translate="no">' + esc(label || 'Call') + '</span></span>' +
      '<a href="' + browserHref + '" target="_blank" rel="noopener" style="color:#8ee6ae;font-size:12px;font-weight:800;text-decoration:none">Open in browser ↗</a>' +
      '<button onclick="Calls.closeCall()" style="border:none;background:#333;color:#fff;font-size:16px;width:34px;height:34px;border-radius:17px;cursor:pointer">✕</button>' +
    '</div>' +
    // v126.16 (Matt: "it just freezes… then it said connection failed") — the
    // free Jitsi service will not START a room until a signed-in HOST arrives;
    // in the embed that waiting state can look like a failure. Say so.
    // (Only on the free service — a JaaS join IS the host.)
    (_jaas ? '' :
    '<div style="background:#3a2f14;color:#f5d78e;font-size:11.5px;line-height:1.45;padding:7px 14px;flex-shrink:0">Stuck on &ldquo;waiting&rdquo; or &ldquo;connection failed&rdquo;? The call needs a <b>host</b> first: the host taps <b>Open in browser ↗</b> and signs in with a free Google account — everyone else stays here and connects the moment the host arrives.</div>') +
    '<div id="callsOverlayBody" style="flex:1;min-height:0"></div>';
  document.body.appendChild(overlay);
  loadJitsiApi(function () {
    var body = document.getElementById('callsOverlayBody');
    if (!body) return;
    try {
      var opts = {
        roomName: _jaas ? (_jaas.appId + '/' + room) : room,
        parentNode: body,
        userInfo: { displayName: (myStore() || 'ELC') + ' (ELC)' },
        // v129.7 — p2p OFF (the silent-call fix, 2 Aug field reports). Every
        // store-to-store call is 1:1, and Jitsi's default for exactly-two-
        // participant rooms is to START on the videobridge then switch to a
        // DIRECT peer connection a few seconds in. Store/mall firewalls block
        // direct store↔store traffic, so media died at the switch — "saw the
        // screen for seconds, then black" (Hills), silent calls with working
        // text chat (Arabian Centre→Wafi). Disabling p2p keeps 1:1 calls on
        // 8x8's bridge permanently — the same path that carried those first
        // working seconds. Costs a little latency; changes nothing for 3+.
        configOverwrite: {
          prejoinConfig: { enabled: true },
          p2p: { enabled: false },
          // v129.35 — the SEND cap (see CALL_QUALITY_HEIGHT above). No `min`:
          // pinning an exact height can fail getUserMedia outright on a camera
          // that will not produce it, and a call that does not start is worse
          // than one that starts at the wrong size.
          resolution: CALL_QUALITY_HEIGHT,
          constraints: { video: { height: { ideal: CALL_QUALITY_HEIGHT, max: CALL_QUALITY_HEIGHT } } },
        },
      };
      if (_jaas) opts.jwt = _jaas.jwt;
      _api = new JitsiMeetExternalAPI(_jaas ? _jaas.host : 'meet.jit.si', opts);
      // The RECEIVE cap. It has to be a command after join — it is the slider's
      // own action, and there is no config key for the initial slider position.
      // Wrapped: an API that ever drops the event or the command must not stop
      // the call, it just means this device receives at Jitsi's default.
      try {
        _api.addListener('videoConferenceJoined', function () {
          try { _api.executeCommand('setVideoQuality', CALL_QUALITY_HEIGHT); } catch (e) { /* default quality */ }
        });
      } catch (e) { /* older external_api build — send cap still applies */ }
    } catch (e) {
      body.innerHTML = '<div style="padding:30px;text-align:center;color:#fff">Could not start the embedded call — use <b>Open in browser ↗</b> above.</div>';
    }
  });
}

function closeCall() {
  try { if (_api && _api.dispose) _api.dispose(); } catch (e) { /* already gone */ }
  _api = null;
  var overlay = document.getElementById('callsOverlay');
  if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
}

// v129.35 — join from a standard Worker call response ({ room, label, and the
// jaas_host/app_id/jwt trio when JaaS is configured). Extracted so the Calls
// tab, meeting tasks and the new chat 📹 button all adopt the token the SAME
// way: without this the caller must remember to set _jaas BEFORE joinRoom, and
// a caller that forgets gets a silent fallback to free meet.jit.si — working,
// but with the waiting-for-host warning nobody can debug from.
// Exported so src/chat can join without reaching into this module's state.
function joinFromResponse(d, fallbackLabel, meta) {
  if (!d || !d.room) return;
  _jaas = (d.jwt && d.app_id) ? { host: d.jaas_host || '8x8.vc', appId: d.app_id, jwt: d.jwt } : _jaas;
  _jaasExp = _jaas ? jwtExpSeconds(_jaas.jwt) : 0;
  joinRoom(d.room, d.label || fallbackLabel || 'Call', meta);
}

// joinArea is also the entry point for the Tasks-card 📹 Join button — it
// self-loads rooms so it works before the Calls tab has ever been opened.
// v127.38 (the calls rework thread) — a meeting-task joins ITS OWN derived
// room now, not the area room: the audience is exactly the task's audience
// (an AM picking stores in the composer has picked who can join; HO ad-hoc
// with any audience works the same). The Worker gates the room on task
// visibility, same as /api/tasks/attachment.
async function joinTask(taskId) {
  var st = myStore();
  try {
    var q = '/api/calls/task?task_id=' + encodeURIComponent(taskId) + (st ? '&store=' + encodeURIComponent(st) : '');
    var res = await api(q);
    if (!res.ok) {
      if (typeof showToast !== 'undefined') showToast('Could not open that meeting — sign in to your store first', 'error');
      return;
    }
    var d = await res.json();
    if (!d || !d.ok || !d.room) return;
    joinFromResponse(d, 'Meeting', { kind: 'task', target: 'task#' + taskId });
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('Could not open that meeting — check your connection', 'error');
  }
}

async function joinArea() {
  var d = _rooms || await fetchRooms();
  if (!d || !d.rooms || !d.rooms.area) {
    if (typeof showToast !== 'undefined') showToast('No area call room found — sign in to your store first', 'error');
    return;
  }
  joinRoom(d.rooms.area.room, d.rooms.area.label, { kind: 'area', target: d.rooms.area.label });
}
async function joinStore() {
  var d = _rooms || await fetchRooms();
  if (!d || !d.rooms || !d.rooms.store) return;
  joinRoom(d.rooms.store.room, d.rooms.store.label, { kind: 'store', target: d.store });
}
function joinPicked() {
  var sel = document.getElementById('callsStorePick');
  if (!sel || !sel.value) return;
  var name = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : 'Store';
  joinRoom(sel.value, name + ' room', { kind: 'store_to_store', target: name });
}
// v126.63 — a manager joins one of THEIR stores by index into _myRooms, so the
// room name never travels through an onclick string (mirrors the drill-by-index
// pattern; injection-safe).
function joinMyStore(i) {
  var s = _myRooms[i];
  if (!s || !s.room) return;
  joinRoom(s.room, s.name + ' room', { kind: 'store_to_store', target: s.name });
}

// ── 📞 Ring-through (v128.47) — Teams-style dialing ─────────────────────────
// dial(): POST /api/calls/dial → an outgoing-call modal ("Calling X…"),
// polling /dial-status every 3s. accepted → join the per-call room;
// declined/missed/cancelled → say so and stop. The callee side is
// startRingWatch() below.
var _dial = null;   // { inviteId, toStore, timer } while an outgoing call rings

function dialModalHtml(toStore, note) {
  return '<div style="background:var(--card);border-radius:18px;padding:26px 22px;max-width:320px;width:88%;text-align:center">' +
    '<div style="font-size:42px;margin-bottom:8px">📞</div>' +
    '<div style="font-weight:900;font-size:17px;margin-bottom:4px">Calling <span translate="no">' + esc(toStore) + '</span>…</div>' +
    '<div id="callsDialNote" style="font-size:12.5px;color:var(--ui-ink-2);min-height:18px">' + esc(note || 'Ringing on their screen — hold on') + '</div>' +
    '<button onclick="Calls.cancelDial()" style="margin-top:16px;border:none;background:#d64545;color:#fff;font-weight:800;font-size:14px;padding:11px 26px;border-radius:12px;cursor:pointer">Hang up</button>' +
    '</div>';
}

function closeDialModal() {
  if (_dial && _dial.timer) clearInterval(_dial.timer);
  _dial = null;
  var m = document.getElementById('callsDialModal');
  if (m && m.parentNode) m.parentNode.removeChild(m);
}

// v129.41 — `toStore` is the LABEL on the modal ("Calling Alex…"), and
// opts.accountId switches the target from a store to a person. The whole
// ringing/polling/joining machinery below is identical either way, which is why
// this is a parameter rather than a second copy of it.
async function dial(toStore, opts) {
  if (!toStore || _dial) return;
  var o = opts || {};
  var accountId = o.accountId != null ? Number(o.accountId) : null;
  var st = myStore();
  try {
    var res = await api('/api/calls/dial' + (st ? '?store=' + encodeURIComponent(st) : ''), {
      method: 'POST',
      body: JSON.stringify(accountId
        ? { to_account_id: accountId, store: st }
        : { to_store: toStore, store: st }),
    });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d.ok) {
      if (typeof showToast !== 'undefined') showToast((d && d.error) || ('Could not ring ' + toStore), 'error');
      return;
    }
    if (d.jwt && d.app_id) { _jaas = { host: d.jaas_host || '8x8.vc', appId: d.app_id, jwt: d.jwt }; _jaasExp = jwtExpSeconds(_jaas.jwt); }
    var modal = document.createElement('div');
    modal.id = 'callsDialModal';
    modal.style.cssText = 'position:fixed;inset:0;z-index:9998;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center';
    modal.innerHTML = dialModalHtml(toStore);
    document.body.appendChild(modal);
    _dial = { inviteId: d.invite_id, toStore: toStore, room: d.room, label: d.label };
    _dial.timer = setInterval(async function () {
      if (!_dial) return;
      try {
        var sres = await api('/api/calls/dial-status?invite_id=' + _dial.inviteId + (st ? '&store=' + encodeURIComponent(st) : ''));
        var sd = await sres.json().catch(function () { return {}; });
        var status = sd && sd.status;
        if (status === 'accepted') {
          var room = _dial.room, label = _dial.label;
          closeDialModal();
          joinRoom(room, label, { kind: 'ring', target: toStore });
        } else if (status === 'declined' || status === 'missed' || status === 'cancelled') {
          closeDialModal();
          if (typeof showToast !== 'undefined') {
            showToast(status === 'declined' ? toStore + ' declined the call' : 'No answer from ' + toStore + ' — try 💬 Chat', 'info');
          }
        }
        // still ringing → keep polling
      } catch (e) { /* transient — keep ringing */ }
    }, 3000);
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('Could not reach the calls service — check the connection', 'error');
  }
}

function cancelDial() {
  var inv = _dial && _dial.inviteId;
  var st = myStore();
  closeDialModal();
  if (inv) {
    api('/api/calls/answer' + (st ? '?store=' + encodeURIComponent(st) : ''), {
      method: 'POST', body: JSON.stringify({ invite_id: inv, action: 'cancel', store: st }),
    }).catch(function () {});
  }
}

function dialPicked() {
  var sel = document.getElementById('callsStorePick');
  if (!sel || !sel.value) return;
  dial(sel.value);
}
function dialMyStore(i) {
  var s = _myRooms[i];
  if (s && s.name) dial(s.name);
}

// v129.41 — ring ONE person at Head Office (📇 Contacts calls this). The name is
// only the modal label; the account id is what actually rings.
function dialPerson(accountId, name) {
  var id = Number(accountId);
  if (!id) return;
  dial(name || 'Head Office', { accountId: id });
}

// ── The callee side: the incoming-call overlay ──────────────────────────────
// A 6s poll (visible tab only, signed-in store only, never during a live
// call) asks /api/calls/ring. An invite pops a full-screen Accept/Decline —
// the Teams moment. Push nudges phones in parallel (notify.js), but THIS
// poll is the ring that always works on the till.
var _ringTimer = null;
var _ringShowing = null;   // invite id currently on screen

// ── v129.120 — the overlay now SOUNDS like a call ───────────────────────────
// The stores' words were "you can't WhatsApp call", and the missing half was
// this: the overlay appeared with ONE short buzz and then sat there silently.
// On a till across the shop that is not a ring, it is a popup nobody saw.
//
// The tone is GENERATED with WebAudio (two-tone bell, UK ring cadence-ish:
// ~1.2s on, ~2s off) — no audio asset, nothing to load, CSP-clean. Phones also
// re-vibrate on each cadence. Everything stops the moment the overlay closes,
// whichever way it closes.
//
// Browsers may refuse audio before the person has ever interacted with the
// page (autoplay policy). That is caught and vibration still runs — and the
// 12s poll + push were already the guarantee the CALL is seen; the tone is
// what makes it FELT.
var _ringAudio = null;   // { ctx, timer } while the overlay is ringing

function ringToneBurst(actx) {
  try {
    var o1 = actx.createOscillator(), o2 = actx.createOscillator(), g = actx.createGain();
    o1.frequency.value = 440; o2.frequency.value = 480;   // the classic ring pair
    g.gain.value = 0.0001;
    o1.connect(g); o2.connect(g); g.connect(actx.destination);
    var t = actx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.05);  // gentle, not an alarm
    g.gain.setValueAtTime(0.12, t + 1.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.25);
    o1.start(t); o2.start(t);
    o1.stop(t + 1.3); o2.stop(t + 1.3);
  } catch (e) { /* one burst failing must not kill the cadence */ }
}

function startRingSound() {
  if (_ringAudio) return;
  var AC = window.AudioContext || window.webkitAudioContext;
  var actx = null;
  try { actx = AC ? new AC() : null; if (actx && actx.state === 'suspended') actx.resume().catch(function () {}); } catch (e) { actx = null; }
  var tick = function () {
    if (actx) ringToneBurst(actx);
    try { if (navigator.vibrate) navigator.vibrate([300, 150, 300]); } catch (e) { /* not a phone */ }
  };
  tick();                                       // ring NOW, then keep the cadence
  _ringAudio = { ctx: actx, timer: setInterval(tick, 3200) };
}

function stopRingSound() {
  if (!_ringAudio) return;
  if (_ringAudio.timer) clearInterval(_ringAudio.timer);
  try { if (_ringAudio.ctx) _ringAudio.ctx.close(); } catch (e) { /* already closed */ }
  try { if (navigator.vibrate) navigator.vibrate(0); } catch (e) { /* not a phone */ }
  _ringAudio = null;
}

function ringOverlayHtml(inv) {
  return '<div style="background:var(--card);border-radius:20px;padding:30px 24px;max-width:340px;width:90%;text-align:center;animation:callsPulse 1.2s ease-in-out infinite alternate">' +
    '<div style="font-size:48px;margin-bottom:8px">📞</div>' +
    '<div style="font-weight:900;font-size:19px;margin-bottom:2px"' + (inv.from ? ' translate="no">' + esc(inv.from) : '>Another store') + '</div>' +
    '<div style="font-size:13px;color:var(--ui-ink-2);margin-bottom:18px">is calling ' + (myStore() ? 'your store' : 'Head Office') + '</div>' +
    '<div style="display:flex;gap:12px;justify-content:center">' +
      '<button onclick="Calls.declineRing()" style="border:none;background:#d64545;color:#fff;font-weight:800;font-size:14px;padding:13px 24px;border-radius:14px;cursor:pointer">Decline</button>' +
      '<button onclick="Calls.acceptRing()" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:14px;padding:13px 24px;border-radius:14px;cursor:pointer">📹 Accept</button>' +
    '</div></div>';
}

function closeRingOverlay() {
  stopRingSound();   // v129.120 — however the overlay closes, the sound stops
  _ringShowing = null;
  var o = document.getElementById('callsRingOverlay');
  if (o && o.parentNode) o.parentNode.removeChild(o);
}

var _ringInvite = null;   // the live invite shown in the overlay

function showRing(d) {
  if (_ringShowing === d.invite.id) return;
  closeRingOverlay();
  _ringShowing = d.invite.id;
  _ringInvite = d.invite;
  if (d.jwt && d.app_id) { _jaas = { host: d.jaas_host || '8x8.vc', appId: d.app_id, jwt: d.jwt }; _jaasExp = jwtExpSeconds(_jaas.jwt); }
  var o = document.createElement('div');
  o.id = 'callsRingOverlay';
  o.style.cssText = 'position:fixed;inset:0;z-index:9998;background:rgba(0,0,0,.65);display:flex;align-items:center;justify-content:center';
  o.innerHTML = ringOverlayHtml(d.invite);
  document.body.appendChild(o);
  startRingSound();   // v129.120 — a repeating ring + vibration, not one buzz
}

async function pollRing() {
  try {
    if (document.visibilityState !== 'visible') return;
    if (_api || _dial) return;                     // in a call / dialing — don't ring over it
    var st = myStore();
    // v129.35 — no store + a manager session = Head Office, and the Worker
    // serves them the shared HO queue off the Bearer identity. This used to
    // `return` here, which is why HO could not be rung at all.
    if (!st && !isHeadOfficeView()) return;
    var res = await api('/api/calls/ring' + (st ? '?store=' + encodeURIComponent(st) : ''));
    if (!res.ok) return;
    var d = await res.json().catch(function () { return {}; });
    if (d && d.ok && d.invite && d.invite.room) showRing(d);
    else if (_ringShowing) closeRingOverlay();     // caller hung up / ring expired
  } catch (e) { /* offline — next tick */ }
}

function startRingWatch() {
  if (_ringTimer) return;
  // v129.93 (request-volume audit): 6s → 12s. The ring poll was the app's
  // single biggest request stream (~7,200/device/day visible) — the 8 Aug
  // 87%-of-cap driver. RING_SECONDS is 45 on the Worker, so a ring is still
  // caught with ≥33s left to answer; halving the rate costs nothing a caller
  // can feel. loadMissed 2min → 5min for the same reason (it is a history
  // list, not a live signal).
  _ringTimer = setInterval(pollRing, 12000);
  loadMissed();                                   // v129.43 — and what you missed
  if (!_missedTimer) _missedTimer = visiblePoll(loadMissed, 300000);   // v136.8: rests while the page is hidden, one catch-up on return
}

// ── v129.43 — 📞 Missed calls ───────────────────────────────────────────────
// Matt, 4 Aug: "do missed calls first."
//
// A ring nobody answered used to vanish. The caller was told "no answer"; the
// person who missed it was told NOTHING. Every unanswered ring was silently
// lost — the single thing that stopped this feeling like a phone system.
//
// No new storage: the ring path has stamped status='missed' since v128.47 and
// call_invites is kept 90 days. This only reads it.
//
// The "seen" watermark is DELIBERATELY per-device localStorage, not a server
// column. A till is one device, which is the case that matters; the trade is
// that a Head Office person on both a phone and a laptop clears the badge
// twice. Worth it to avoid a migration and a write on every glance — and the
// LIST is there either way. Only the NUDGE is dismissible: history is history,
// a badge is a prompt.
var _missed = [];
var _missedTimer = null;
var MISSED_SEEN_KEY = 'elcCallsMissedSeen';

function missedSeenAt() {
  try { return localStorage.getItem(MISSED_SEEN_KEY) || ''; } catch (e) { return ''; }
}
function unseenMissed() {
  var seen = missedSeenAt();
  return _missed.filter(function (m) { return !seen || String(m.at || '') > seen; });
}

async function loadMissed() {
  try {
    var st = myStore();
    if (!st && !isHeadOfficeView()) return;
    var res = await api('/api/calls/missed' + (st ? '?store=' + encodeURIComponent(st) : ''));
    if (!res.ok) return;
    var d = await res.json().catch(function () { return {}; });
    if (!d || !d.ok || !Array.isArray(d.missed)) return;
    _missed = d.missed;
    paintMissedBadge();
    // Repaint in place when the Calls screen is already open, so a miss that
    // lands while you are looking at it appears without a tab bounce.
    var box = document.getElementById('callsMissedBox');
    if (box) box.outerHTML = missedHtml();
  } catch (e) { /* offline — next tick */ }
}

// The 📞 nav pill — the same shape chat/news/tasks already use, in red because
// this one is "you have already missed something", not "there is new content".
function paintMissedBadge() {
  var n = unseenMissed().length;
  var nav = document.getElementById('nav-calls');
  if (!nav) return;
  nav.classList.toggle('nav-attn', n > 0);
  var b = document.getElementById('navCallsPill');
  if (n > 0) {
    if (!b) {
      b = document.createElement('span');
      b.id = 'navCallsPill';
      b.style.cssText = 'position:absolute;top:2px;inset-inline-end:14px;min-width:16px;height:16px;border-radius:8px;background:#d64545;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 4px';
      nav.style.position = 'relative';
      nav.appendChild(b);
    }
    b.textContent = n > 9 ? '9+' : n;
  } else if (b) { b.remove(); }
}

function whenLabel(iso) {
  try {
    var t = new Date(iso).getTime();
    if (!t) return '';
    var mins = Math.round((Date.now() - t) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm ago';
    var hrs = Math.round(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    return new Date(iso).toLocaleDateString();
  } catch (e) { return ''; }
}

function missedHtml() {
  if (!_missed.length) return '<div id="callsMissedBox"></div>';
  var unseen = unseenMissed().length;
  return '<div id="callsMissedBox" style="background:var(--comms-red-faint);border:1.5px solid var(--comms-red-line);border-radius:14px;padding:14px;margin-bottom:12px">' +
    '<div style="display:flex;align-items:center;gap:8px;margin-bottom:2px">' +
      '<div style="font-weight:900;font-size:14px;flex:1">📞 Missed calls' +
        (unseen ? ' <span style="background:#d64545;color:#fff;border-radius:8px;font-size:11px;padding:1px 7px;vertical-align:middle">' + unseen + ' new</span>' : '') +
      '</div>' +
      (unseen ? '<button onclick="Calls.clearMissed()" style="border:none;background:none;color:var(--ui-ink-2);font-size:11.5px;font-weight:700;cursor:pointer;font-family:inherit">Mark as seen</button>' : '') +
    '</div>' +
    '<div style="font-size:12px;color:var(--ui-ink-2);margin-bottom:6px">Last 24 hours. Tap to ring them back.</div>' +
    _missed.map(function (m, i) {
      // Ring back by INDEX — a caller's name never rides an onclick string
      // (the same injection rule the store list follows).
      return '<div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--comms-red-rule)">' +
        '<span style="font-size:16px">📵</span>' +
        '<div style="flex:1;min-width:0">' +
          '<div style="font-weight:700;font-size:13.5px" translate="no">' + esc(m.from) + '</div>' +
          '<div style="font-size:11.5px;color:var(--ui-ink-2)">' + esc(whenLabel(m.at)) + '</div>' +
        '</div>' +
        (m.call_store
          ? '<button onclick="Calls.callBack(' + i + ')" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:12.5px;padding:7px 13px;border-radius:10px;cursor:pointer">📞 Ring back</button>'
          // A Head Office caller has no store to ring back, and from_name is a
          // LABEL not an id — guessing a person from it is how you ring the
          // wrong Ahmed. Say what it was instead of offering a button that
          // reaches somebody else.
          : '<span style="font-size:11px;color:var(--comms-hint)">Head Office</span>') +
        '</div>';
    }).join('') +
    '</div>';
}

function clearMissed() {
  var newest = _missed.length ? String(_missed[0].at || '') : new Date().toISOString();
  try { localStorage.setItem(MISSED_SEEN_KEY, newest); } catch (e) { /* private mode */ }
  paintMissedBadge();
  var box = document.getElementById('callsMissedBox');
  if (box) box.outerHTML = missedHtml();
}

function callBack(i) {
  var m = _missed[i];
  if (m && m.call_store) dial(m.call_store);
}

async function acceptRing() {
  var inv = _ringInvite;
  closeRingOverlay();
  if (!inv) return;
  var st = myStore();
  try {
    var res = await api('/api/calls/answer' + (st ? '?store=' + encodeURIComponent(st) : ''), {
      method: 'POST', body: JSON.stringify({ invite_id: inv.id, action: 'accept', store: st }),
    });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d.ok) {
      if (typeof showToast !== 'undefined') showToast((d && d.error) || 'That call already ended', 'info');
      return;
    }
    if (d.jwt && d.app_id) { _jaas = { host: d.jaas_host || '8x8.vc', appId: d.app_id, jwt: d.jwt }; _jaasExp = jwtExpSeconds(_jaas.jwt); }
    joinRoom(d.room || inv.room, d.label || ('Call with ' + (inv.from || 'another store')), { kind: 'ring', target: inv.from || null });
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('Could not answer — check the connection', 'error');
  }
}

function declineRing() {
  var inv = _ringInvite;
  closeRingOverlay();
  if (!inv) return;
  var st = myStore();
  api('/api/calls/answer' + (st ? '?store=' + encodeURIComponent(st) : ''), {
    method: 'POST', body: JSON.stringify({ invite_id: inv.id, action: 'decline', store: st }),
  }).catch(function () {});
}

// Self-start (the achievements boot pattern): wait for the store sign-in,
// then watch for rings for the life of the tab.
// v129.35 — a manager session with no store starts the watch too, so Head
// Office is reachable.
//
// v129.123/124 — WHICH page rings is now the host page's decision, declared as
// `window.CALLS_RING_WATCH = true`.
//
// The note that used to sit here said "admin.html loads this bundle as well;
// there `currentUser` is not defined". The second half was true and load-bearing;
// the first was not — admin.html did not load this bundle at all, which is why
// the Head Office chat header had no 📹. v129.123 added that script tag, so the
// assumption went live, and a comment is the wrong place to keep a safety
// property. There are THREE host pages, not two, and they want different things:
//
//   index.html  — the till / staff PWA. Rings. (Always has.)
//   chat.html   — the standalone chat app Head Office INSTALLS on their phones.
//                 Rings, as of v129.124: it is the whole reason "Head Office can
//                 be called" should be true.
//   admin.html  — the desktop console. Does NOT ring. A ring is something you
//                 answer on the device in your hand, and an overlay thrown over
//                 a manager mid-edit is not an improvement. It loads the bundle
//                 only so the chat 📹 can open a room.
//
// An opt-in flag also fixes the quieter half: without it this interval ticks
// every 3s for the life of an admin tab, waiting for a sign-in that page never
// performs.
function wantsRingWatch() {
  try { return typeof window !== 'undefined' && window.CALLS_RING_WATCH === true; } catch (e) { return false; }
}
var _ringBoot = setInterval(function () {
  if (typeof document === 'undefined') return;
  if (!wantsRingWatch()) {
    // Not this page's job. Only give up once the DOM is settled — the flag is
    // set by an inline script that may not have run when an early tick fires.
    if (document.readyState === 'complete') clearInterval(_ringBoot);
    return;
  }
  if (myStore() || isHeadOfficeView()) { clearInterval(_ringBoot); startRingWatch(); }
}, 3000);

// v126.16 — ad-hoc meetings: derive the room from the typed name (Worker-side
// HMAC, same secret) so the audience is simply whoever knows the name.
async function joinCustom() {
  var input = document.getElementById('callsMeetingName');
  var name = input ? String(input.value || '').trim() : '';
  if (name.length < 3) { if (typeof showToast !== 'undefined') showToast('Give the meeting a name (3+ characters)', 'error'); return; }
  try {
    var st = myStore();
    var res = await api('/api/calls/custom?name=' + encodeURIComponent(name) + (st ? '&store=' + encodeURIComponent(st) : ''));
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d.ok) { if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not set the meeting up', 'error'); return; }
    if (d.jwt && d.app_id) { _jaas = { host: d.jaas_host || '8x8.vc', appId: d.app_id, jwt: d.jwt }; _jaasExp = jwtExpSeconds(_jaas.jwt); }   // v126.77
    joinRoom(d.room, d.label, { kind: 'custom', target: d.name });
  } catch (e) { if (typeof showToast !== 'undefined') showToast('Could not reach the calls service — check the connection', 'error'); }
}


// ── 📶 v129.75 — the store connection check (Matt: stores keep reporting
// "can't use the video" — almost always bandwidth). One tap: measures
// latency, download and upload against the Worker (the one host every till
// can reach) and answers in plain words — you're fine / reset the router /
// talk to IT. HONEST SCOPE: this measures the store line to Cloudflare, not
// to the video service — so a GOOD result with video still failing means the
// network is blocking the video service itself, and the verdict says so.

// Pure: thresholds sized for our calls (video quality is capped at 360p —
// v129.35 — so ~1.2 Mbps each way is genuinely usable; 3/1.5 gives headroom
// for a busy till). Exported for tests.
function computeNetVerdict(m) {
  var down = Number(m && m.downMbps) || 0;
  var up = Number(m && m.upMbps) || 0;
  var ping = Number(m && m.pingMs);
  if (down >= 3 && up >= 1.5 && (isNaN(ping) || ping < 400)) {
    return { level: 'ok', icon: '\u2705', title: 'Connection is fine for video calls',
      advice: 'Speed is not the problem. If video still will not start, the store network is likely blocking the video service \u2014 ask IT to allow video calling on this connection (8x8.vc and meet.jit.si).' };
  }
  if (down >= 1.2 && up >= 0.6) {
    return { level: 'slow', icon: '\u26a0\ufe0f', title: 'Slow \u2014 video may freeze or drop',
      advice: 'Enough for audio, marginal for video' + (!isNaN(ping) && ping >= 400 ? ' (and the line is laggy)' : '') + '. Turn the router off for 30 seconds, turn it back on, and run this test again. If it stays like this, talk to IT.' };
  }
  return { level: 'bad', icon: '\u274c', title: 'Too slow for video calls',
    advice: 'Turn the router off for 30 seconds, turn it back on, and test again. If it is still this slow, the line itself is the problem \u2014 talk to IT.' };
}


// ── v129.76 — device diagnostics (Matt, 7 Aug: "they say the video,
// microphone or sound doesn't work — when I investigate there is no webcam,
// no microphone or no speaker output"). The check now tests the KIT before
// the LINE: camera, microphone (with a real did-we-hear-anything level
// check), speakers (a test beep + a did-you-hear-it button — audibility
// cannot be detected in code, so we ask), then the internet speed.

// Pure: count what the browser can see. Labels are hidden until permission
// is granted, but device COUNTS are always visible. Exported for tests.
function deviceSummary(list) {
  var out = { cams: 0, mics: 0, outs: 0 };
  (Array.isArray(list) ? list : []).forEach(function (d) {
    if (!d) return;
    if (d.kind === 'videoinput') out.cams++;
    else if (d.kind === 'audioinput') out.mics++;
    else if (d.kind === 'audiooutput') out.outs++;
  });
  return out;
}

// Pure: turn a getUserMedia failure into the plain-words fix. kind is
// 'camera' or 'microphone'. Exported for tests.
function describeMediaError(errName, kind) {
  var thing = kind === 'camera' ? 'webcam' : 'microphone';
  if (errName === 'NotFoundError' || errName === 'DevicesNotFoundError') {
    return 'No ' + thing + ' found \u2014 check it is plugged in (try a different USB port) and appears in Windows. If the till has never had one, it needs one for video calls.';
  }
  if (errName === 'NotAllowedError' || errName === 'PermissionDeniedError' || errName === 'SecurityError') {
    return 'The ' + thing + ' is BLOCKED, not broken \u2014 click the padlock next to the address bar and allow Camera and Microphone for this site, and check Windows Settings \u2192 Privacy \u2192 ' + (kind === 'camera' ? 'Camera' : 'Microphone') + ' allows apps to use it.';
  }
  if (errName === 'NotReadableError' || errName === 'TrackStartError' || errName === 'AbortError') {
    return 'A ' + thing + ' exists but another program is holding it (Teams/Zoom/another tab?) or the driver has stalled \u2014 close other apps, or unplug/replug it.';
  }
  return 'The ' + thing + ' test failed (' + errName + ') \u2014 unplug/replug it and try again; if it keeps failing, talk to IT.';
}

async function testCamera() {
  try {
    var stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(function (t) { t.stop(); });
    return { ok: true, line: '\u2705 Camera works' };
  } catch (e) {
    return { ok: false, line: '\u274c Camera: ' + describeMediaError(e && e.name, 'camera') };
  }
}

async function testMic() {
  var stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    return { ok: false, line: '\u274c Microphone: ' + describeMediaError(e && e.name, 'microphone') };
  }
  // The mic can EXIST but be muted/dead — listen for ~1.5s and measure the
  // actual signal peak. The UI asks the person to say something first.
  try {
    var AC = window.AudioContext || window.webkitAudioContext;
    var ac = new AC();
    var srcNode = ac.createMediaStreamSource(stream);
    var an = ac.createAnalyser();
    an.fftSize = 2048;
    srcNode.connect(an);
    var buf = new Uint8Array(an.fftSize);
    var peak = 0;
    var t0 = performance.now();
    await new Promise(function (res) {
      (function loop() {
        an.getByteTimeDomainData(buf);
        for (var i = 0; i < buf.length; i++) { var v = Math.abs(buf[i] - 128); if (v > peak) peak = v; }
        if (performance.now() - t0 > 1500) res(); else requestAnimationFrame(loop);
      })();
    });
    try { ac.close(); } catch (e2) { /* best-effort */ }
    stream.getTracks().forEach(function (t) { t.stop(); });
    if (peak / 128 > 0.02) return { ok: true, line: '\u2705 Microphone works \u2014 we heard you' };
    return { ok: false, line: '\u26a0\ufe0f Microphone found but we heard NOTHING \u2014 it may be muted (check the mute switch/keyboard key and Windows sound settings \u2192 Input level).' };
  } catch (e) {
    stream.getTracks().forEach(function (t) { t.stop(); });
    return { ok: true, line: '\u2705 Microphone found (could not measure the level on this browser)' };
  }
}

// The beep. Audibility cannot be detected in code — we play a clear tone and
// ask. The Yes/No answer writes the verdict line.
function playBeep() {
  try {
    var AC = window.AudioContext || window.webkitAudioContext;
    var ac = new AC();
    var o = ac.createOscillator();
    var g = ac.createGain();
    g.gain.value = 0.4;
    o.frequency.value = 880;
    o.connect(g); g.connect(ac.destination);
    o.start();
    setTimeout(function () { try { o.stop(); ac.close(); } catch (e) { /* done */ } }, 700);
    return true;
  } catch (e) { return false; }
}

function spkAnswer(heard) {
  var el = document.getElementById('spkLine');
  if (!el) return;
  el.innerHTML = heard
    ? '<div style="font-size:12.5px;font-weight:700;color:var(--comms-ok-ink)">\u2705 Speakers work \u2014 you heard the beep</div>'
    : '<div style="font-size:12.5px;font-weight:700;color:var(--comms-red)">\u274c No sound heard \u2014 check: volume up + not muted (the speaker icon by the Windows clock), the right output picked (Windows sound settings \u2192 Output), speakers/headset actually plugged in. If a monitor without speakers is the output, that is the problem.</div>';
}

function spkAskHtml(beeped) {
  if (!beeped) {
    return '<div style="font-size:12.5px;font-weight:700;color:var(--comms-amber-ink)">\u26a0\ufe0f Could not play the test sound on this browser \u2014 check the volume icon by the Windows clock instead.</div>';
  }
  return '<div style="font-size:12.5px;font-weight:700">\ud83d\udd0a Did you hear the beep?</div>' +
    '<div style="display:flex;gap:8px;margin-top:4px">' +
    '<button onclick="Calls.spkYes()" style="border:1px solid var(--comms-ok-ink);background:var(--ui-green-soft);color:var(--comms-ok-ink);font-weight:800;font-size:12.5px;padding:7px 14px;border-radius:8px;cursor:pointer">Yes</button>' +
    '<button onclick="Calls.spkNo()" style="border:1px solid var(--comms-red);background:var(--comms-red-soft);color:var(--comms-red);font-weight:800;font-size:12.5px;padding:7px 14px;border-radius:8px;cursor:pointer">No</button></div>';
}
function spkYes() { spkAnswer(true); }
function spkNo() { spkAnswer(false); }

var _netTesting = false;
async function netCheck() {
  if (_netTesting) return;
  var box = document.getElementById('netCheckResult');
  if (!box) return;
  _netTesting = true;
  var lines = [];
  var paint = function (extra) {
    box.innerHTML = lines.map(function (l) { return '<div style="font-size:12.5px;font-weight:700;line-height:1.5;padding:2px 0">' + l + '</div>'; }).join('') + (extra || '');
  };
  try {
    // ── v129.76: the KIT first — camera, mic, speakers ──
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      paint('<div style="font-size:12px;color:var(--ui-ink-2)">\u23f3 Checking camera\u2026</div>');
      var cam = await testCamera();
      lines.push(cam.line);
      paint('<div style="font-size:12px;color:var(--ui-ink-2)">\u23f3 Checking microphone \u2014 <b>say something out loud now</b>\u2026</div>');
      var mic = await testMic();
      lines.push(mic.line);
      var devs = deviceSummary(await navigator.mediaDevices.enumerateDevices().catch(function () { return []; }));
      if (devs.outs === 0) {
        lines.push('\u26a0\ufe0f Windows reports NO speaker output at all \u2014 plug in speakers or a headset, or pick an output in Windows sound settings.');
      }
      var beeped = playBeep();
      lines.push('<span id="spkLine">' + spkAskHtml(beeped) + '</span>');
    } else {
      lines.push('\u26a0\ufe0f This browser cannot test the camera/microphone \u2014 checking the internet only.');
    }
    var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    // Latency: median of 3 tiny requests (first one also warms the connection).
    paint('<div style="font-size:12px;color:var(--ui-ink-2)">\u23f3 Testing internet\u2026 response time</div>');
    var pings = [];
    for (var i = 0; i < 3; i++) {
      var t0 = performance.now();
      var pr = await fetch(base + '/api/nettest/ping', { cache: 'no-store' });
      if (!pr.ok) throw new Error('ping ' + pr.status);
      await pr.text();
      pings.push(performance.now() - t0);
    }
    pings.sort(function (a, b) { return a - b; });
    var pingMs = Math.round(pings[1]);
    // Download: 2 MB timed.
    paint('<div style="font-size:12px;color:var(--ui-ink-2)">\u23f3 Testing internet\u2026 download</div>');
    var d0 = performance.now();
    var dres = await fetch(base + '/api/nettest/down?bytes=2097152', { cache: 'no-store' });
    if (!dres.ok) throw new Error('download ' + dres.status);
    var dbuf = await dres.arrayBuffer();
    var downSecs = (performance.now() - d0) / 1000;
    var downMbps = Math.round((dbuf.byteLength * 8 / downSecs / 1e6) * 10) / 10;
    // Upload: 1 MB timed.
    paint('<div style="font-size:12px;color:var(--ui-ink-2)">\u23f3 Testing internet\u2026 upload</div>');
    var up0 = performance.now();
    var ures = await fetch(base + '/api/nettest/up', { method: 'POST', cache: 'no-store', body: new Uint8Array(1048576) });
    if (!ures.ok) throw new Error('upload ' + ures.status);
    await ures.json();
    var upSecs = (performance.now() - up0) / 1000;
    var upMbps = Math.round((1048576 * 8 / upSecs / 1e6) * 10) / 10;
    var v = computeNetVerdict({ downMbps: downMbps, upMbps: upMbps, pingMs: pingMs });
    var col = v.level === 'ok' ? 'var(--comms-ok-ink)' : (v.level === 'slow' ? 'var(--comms-amber-ink)' : 'var(--comms-red)');
    lines.push(
      '<div style="display:flex;gap:14px;flex-wrap:wrap;font-size:13px;font-weight:800;padding:6px 0 2px">' +
        '<span>\u2b07\ufe0f ' + downMbps + ' Mbps</span>' +
        '<span>\u2b06\ufe0f ' + upMbps + ' Mbps</span>' +
        '<span>\u23f1 ' + pingMs + ' ms</span></div>' +
      '<div style="font-size:13px;font-weight:800;color:' + col + '">' + v.icon + ' ' + v.title + '</div>' +
      '<div style="font-size:12px;color:var(--ui-ink-2);line-height:1.5;font-weight:400">' + v.advice + '</div>');
    paint();
  } catch (e) {
    lines.push('<div style="font-size:12.5px;color:var(--comms-red);font-weight:700">\u274c Could not test the internet \u2014 it may be down entirely.</div>' +
      '<div style="font-size:12px;color:var(--ui-ink-2);line-height:1.5;font-weight:400;margin-top:2px">Check other sites load on this till. If nothing loads: turn the router off for 30 seconds and back on; if it is still dead, talk to IT. (' + String(e && e.message || e).replace(/[<>]/g, '') + ')</div>');
    paint();
  } finally {
    _netTesting = false;
  }
}

function netCheckCardHtml() {
  return '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:10px">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;gap:10px">' +
      '<div><div style="font-weight:900;font-size:14px">\ud83e\ude7a Video call check</div>' +
      '<div style="font-size:12px;color:var(--ui-ink-2)">Camera, microphone, sound and internet \u2014 one tap tells you what is wrong. Allow camera/mic if the browser asks.</div></div>' +
      '<button onclick="Calls.netCheck()" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:10px 14px;border-radius:10px;cursor:pointer;white-space:nowrap">Run test</button>' +
    '</div><div id="netCheckResult"></div></div>';
}

export {
  render, joinRoom, joinFromResponse, joinArea, joinStore, joinPicked, joinMyStore, joinCustom, joinTask, closeCall, dismissIntro,
  dial, dialPicked, dialMyStore, dialPerson, cancelDial, startRingWatch, pollRing, acceptRing, declineRing,
  loadMissed, clearMissed, callBack,
  netCheck, computeNetVerdict,               // v129.75 — 📶 connection check
  deviceSummary, describeMediaError, spkYes, spkNo,   // v129.76 — 🩺 device diagnostics
};
